import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './CountryIndex.scss';

interface Country {
  id: number;
  name: string;
  countryType: number;
  symbol: string;
  phoneCode?: string;
  currency: string;
  isActive: boolean;
  isSuspended: boolean;
  createdAt: string;
  updatedAt: string;
}

export const CountryIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['countries', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchCountries = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/country?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('countries:messages.fetchError'));
        return;
      }

      setCountries(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error) {
      showErrorToast(t('countries:messages.fetchError'));
      console.error('Error fetching countries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);
  const getCountryType = (type: number): string => {
    switch (type) {
      case 0:
        return t('countries:types.both');
      case 1:
        return t('countries:types.destination');
      case 2:
        return t('countries:types.source');
      default:
        return t('countries:types.notSet');
    }
  };

  const getDisplayName = (name: string): string => {
    try {
      // Try to parse as JSON
      if (name && name.startsWith('{')) {
        const parsed = JSON.parse(name);
        return parsed.en || parsed.ar || name;
      }
    } catch (e) {
      // Not valid JSON, return as-is
    }
    return name;
  };

  const deleteCountry = async (id: number) => {
    if (!window.confirm(t('countries:messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await adminApi.call(`/country/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('countries:messages.deleteError'));
        return;
      }

      showSuccessToast(t('countries:messages.deleteSuccess'));
      fetchCountries(currentPage, pageSize);
    } catch (error) {
      showErrorToast(t('countries:messages.deleteError'));
      console.error('Error deleting country:', error);
    }
  };

  const handleCreateCountry = () => {
    navigate('/countries/create');
  };

  const handleEditCountry = (id: number) => {
    navigate(`/countries/edit/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('countries:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
      render: (value) => getDisplayName(value),
    },
    {
      key: 'countryType',
      title: t('countries:table.type'),
      sortable: true,
      render: (value) => getCountryType(value),
    },
    {
      key: 'symbol',
      title: t('countries:table.symbol'),
      sortable: true,
      filterable: true,
      filterKey: 'symbol',
    },
    {
      key: 'phoneCode',
      title: t('countries:table.phoneCode'),
      sortable: true,
      filterable: true,
      filterKey: 'phoneCode',
      render: (value) => value || '-',
    },
    {
      key: 'currency',
      title: t('countries:table.currency'),
      sortable: true,
      filterable: true,
      filterKey: 'currency',
      render: (value) => value || '-',
    },
    {
      key: 'isActive',
      title: t('countries:table.status'),
      sortable: true,
      width: '100px',
      align: 'center',
      render: (value) => (
        <span className={`badge ${value ? 'bg-success' : 'bg-danger'}`}>
          {value ? t('countries:status.active') : t('countries:status.inactive')}
        </span>
      ),
    },
    {
      key: 'isSuspended',
      title: t('countries:table.suspended'),
      sortable: true,
      width: '100px',
      align: 'center',
      render: (value) => (
        <span className={`badge ${value ? 'bg-warning' : 'bg-secondary'}`}>
          {value ? t('countries:status.suspended') : t('countries:status.notSuspended')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('countries:table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, country) => (
        <div className="action-buttons">
          {hasPermission("Update Country") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditCountry(country.id)}
              tooltip={t('countries:editCountry')}
            />
          )}
          {hasPermission("Delete Country") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteCountry(country.id)}
              tooltip={t('countries:deleteCountry')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('countries:breadcrumbs.countries') }
  ];

  return (
    <PageLayout
      title={t('countries:title')}
      subtitle={t('countries:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={countries}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create Country") ? {
          label: t('countries:addNew'),
          onClick: handleCreateCountry
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchCountries(currentPage, pageSize)}
        emptyMessage={t('countries:messages.noCountries')}
      />
    </PageLayout>
  );
};
